import { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { IProduct } from "redux/reducers/models/product";
import {
  buildBrowseFilters,
  IBrowseFilter,
  parseProductSearchResponse,
  PRODUCT_SEARCH_PAGE_SIZE,
} from "components/folders/productBrowseFilters";

export interface IFolderProductSearchContext {
  search: string;
  browseFilter: IBrowseFilter;
  facilityId?: number;
  clientId?: number;
  types?: string[];
  variants?: boolean;
  track_inventory?: boolean;
  vendor_id?: number;
}

export type FolderProductSearchRequestParams = {
  search: string;
  filters?: ReturnType<typeof buildBrowseFilters>;
  limit: number;
  offset: number;
  facility_id?: number;
  client_id?: number;
  get_count?: boolean;
  types?: string[];
  variants?: boolean;
  track_inventory?: boolean;
  vendor_id?: number;
};

type GetProductFn = (
  params: Record<string, unknown>,
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
) => Promise<{ status: number | StatusCode; data?: unknown }>;

export function getProductSearchRequestParams(
  context: IFolderProductSearchContext,
  offset: number,
  getCount: boolean,
): FolderProductSearchRequestParams {
  return {
    search: context.search,
    filters: buildBrowseFilters(context.browseFilter),
    limit: PRODUCT_SEARCH_PAGE_SIZE,
    offset,
    ...(context.facilityId != null ? { facility_id: context.facilityId } : {}),
    ...(context.clientId != null ? { client_id: context.clientId } : {}),
    ...(context.vendor_id != null ? { vendor_id: context.vendor_id } : {}),
    ...(context.types != null && context.types.length > 0 ? { types: context.types } : {}),
    ...(context.variants ? { variants: true as const } : {}),
    ...(context.track_inventory ? { track_inventory: true as const } : {}),
    ...(getCount ? { get_count: true as const } : {}),
  };
}

export async function fetchFolderProductSearchPage(
  getProduct: GetProductFn,
  context: IFolderProductSearchContext,
  offset: number,
  getCount: boolean,
  cancelToken?: CancelToken,
  onError?: () => void,
) {
  const productRes = await getProduct(
    getProductSearchRequestParams(context, offset, getCount),
    false,
    cancelToken,
  );

  if (Number(productRes.status) !== Number(StatusCode.OK)) {
    if (!cancelToken?.reason) {
      onError?.();
    }
    return { products: [] as IProduct[], totalCount: 0 };
  }

  return parseProductSearchResponse(productRes.data);
}

export function appendUniqueProducts(existing: IProduct[], incoming: IProduct[]) {
  const existingIds = new Set(existing.map(product => product.id));
  const newProducts = incoming.filter(product => !existingIds.has(product.id));
  return [...existing, ...newProducts];
}

export function collectSelectableProducts(
  products: IProduct[],
  excludeIds: Set<number>,
  maxCount: number,
) {
  const collected: IProduct[] = [];

  for (const product of products) {
    if (excludeIds.has(product.id)) {
      continue;
    }

    collected.push(product);
    excludeIds.add(product.id);

    if (collected.length >= maxCount) {
      break;
    }
  }

  return collected;
}

export async function fetchAllMatchingFolderProducts(
  getProduct: GetProductFn,
  context: IFolderProductSearchContext,
  excludeIds: Set<number>,
  maxCount: number,
  onError?: () => void,
  startOffset = 0,
  knownTotalCount?: number,
) {
  if (maxCount <= 0) {
    return [] as IProduct[];
  }

  const collected: IProduct[] = [];
  let offset = startOffset;
  let totalCount = knownTotalCount ?? Infinity;

  while (offset < totalCount && collected.length < maxCount) {
    const page = await fetchFolderProductSearchPage(
      getProduct,
      context,
      offset,
      offset === 0 && knownTotalCount == null,
      undefined,
      onError,
    );

    if (offset === 0 && knownTotalCount == null) {
      totalCount = page.totalCount;
    }

    for (const product of page.products) {
      if (excludeIds.has(product.id)) {
        continue;
      }

      collected.push(product);
      excludeIds.add(product.id);

      if (collected.length >= maxCount) {
        return collected;
      }
    }

    offset += page.products.length;

    if (page.products.length === 0) {
      break;
    }
  }

  return collected;
}

export function sliceSelectAllBatch(
  toAdd: IProduct[],
  productsToAddToFolder: IProduct[],
  availableSlotCount: number,
) {
  if (toAdd.length === 0) {
    return [] as IProduct[];
  }

  const selectedIds = new Set(productsToAddToFolder.map(product => product.id));
  const slots = availableSlotCount - productsToAddToFolder.length;
  return toAdd.filter(product => !selectedIds.has(product.id)).slice(0, slots);
}

export async function buildSelectAllProductBatch(
  getProduct: GetProductFn,
  context: IFolderProductSearchContext,
  loaded: IProduct[],
  totalCount: number,
  excludeIds: Set<number>,
  maxCount: number,
  onError?: () => void,
) {
  const ids = new Set(excludeIds);
  let remaining = maxCount;
  const toAdd = collectSelectableProducts(loaded, ids, remaining);
  remaining -= toAdd.length;

  if (remaining > 0 && loaded.length < totalCount) {
    const fetched = await fetchAllMatchingFolderProducts(
      getProduct,
      context,
      ids,
      remaining,
      onError,
      loaded.length,
      totalCount,
    );
    toAdd.push(...fetched);
  }

  return toAdd;
}
